import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "checking" });
  const [membership, setMembership] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus({ state: "error", message: "Missing session" }); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) { setStatus({ state: "timeout" }); return; }
      attempts.current++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", data });
          if (data.type === "membership") {
            try { const { data: me } = await api.get("/memberships/me"); setMembership(me); } catch {}
          } else if (data.type === "gift_voucher") {
            try { const { data: v } = await api.get(`/gift-vouchers/by-session/${sessionId}`); setVoucher(v); } catch {}
          }
          return;
        }
        if (data.status === "expired") { setStatus({ state: "expired" }); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2500); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const isMembership = status.data?.type === "membership";
  const isVoucher = status.data?.type === "gift_voucher";

  return (
    <div data-testid="payment-success-page" className="bg-[#F5EFE4] min-h-[80vh]">
      <div className="max-w-[900px] mx-auto px-6 pt-40 pb-24 text-center">
        {status.state === "paid" ? (
          <>
            <div className="w-16 h-16 rounded-full bg-[#C5A059] text-white flex items-center justify-center mx-auto"><CheckCircle2 size={30} /></div>
            <p className="overline mt-6">{isVoucher ? "Voucher Issued" : isMembership ? "Welcome" : "Confirmed"}</p>
            <h1 className="font-serif text-5xl lg:text-6xl mt-4 text-[#1A1815]">
              {isVoucher ? "Your gift voucher." : isMembership ? "Welcome to Luxor." : "Thank you."}
            </h1>
            {isVoucher && voucher ? (
              <>
                <p className="text-[#6B665E] mt-4 max-w-lg mx-auto">
                  A <strong className="text-[#1A1815]">£{Number(voucher.amount).toFixed(0)}</strong> Luxor gift voucher has been issued. The code below has also been sent to your email{voucher.recipient_email ? " and the recipient" : ""}.
                </p>
                <div className="mt-8 mx-auto max-w-md bg-white border border-[#C5A059] p-8 text-center">
                  <p className="overline">Voucher Code</p>
                  <p className="font-serif text-3xl text-[#C5A059] mt-3 tracking-wider" data-testid="issued-voucher-code">{voucher.code}</p>
                  <button type="button" onClick={() => { navigator.clipboard.writeText(voucher.code); toast.success("Code copied"); }} className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#1A1815] hover:text-[#C5A059]" data-testid="copy-voucher-btn">
                    <Copy size={13} /> Copy code
                  </button>
                  <button type="button" onClick={() => window.print()} className="mt-4 ml-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#1A1815] hover:text-[#C5A059]" data-testid="print-voucher-btn">
                    Print / Save PDF
                  </button>
                </div>
              </>
            ) : isMembership && membership?.membership_code ? (
              <>
                <p className="text-[#6B665E] mt-4 max-w-lg mx-auto">
                  Your <strong className="text-[#1A1815]">{membership.details?.name}</strong> membership is now active. Below is your personal LSC code — keep it safe.
                </p>
                <div className="mt-8 mx-auto max-w-md bg-white border border-[#C5A059] p-8 text-center">
                  <p className="overline">Your Personal Code</p>
                  <p className="font-serif text-4xl text-[#C5A059] mt-3 tracking-wider" data-testid="issued-membership-code">{membership.membership_code}</p>
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard.writeText(membership.membership_code); toast.success("Code copied"); }}
                    className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#1A1815] hover:text-[#C5A059]"
                    data-testid="copy-code-btn"
                  >
                    <Copy size={13} /> Copy code
                  </button>
                  <p className="mt-4 text-xs text-[#6B665E]">
                    Auto-applied when you book while signed in.
                  </p>
                </div>
              </>
            ) : (
              <p className="text-[#6B665E] mt-4 max-w-lg mx-auto">
                Your reservation has been confirmed. A concierge will be in touch to finalise the small details.
              </p>
            )}
            <div className="mt-10 flex justify-center gap-4 flex-wrap">
              <Link to="/dashboard" className="luxor-btn-primary" data-testid="success-to-dashboard">
                {isMembership ? "Go to dashboard" : "Your reservations"}
              </Link>
              <Link to="/" className="luxor-btn-outline-dark">Return home</Link>
            </div>
          </>
        ) : status.state === "checking" || status.state === "timeout" ? (
          <><p className="overline">Verifying payment</p><h1 className="font-serif text-4xl mt-4 text-[#1A1815]">One moment.</h1><p className="text-[#6B665E] mt-3">Confirming with Stripe…</p></>
        ) : (
          <>
            <p className="overline" style={{ color: "#B91C1C" }}>Payment not completed</p>
            <h1 className="font-serif text-4xl mt-4 text-[#1A1815]">We could not confirm this yet.</h1>
            <p className="text-[#6B665E] mt-3">Please check your dashboard or contact the concierge.</p>
            <Link to="/dashboard" className="luxor-btn-primary mt-8 inline-flex">Go to dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}
